import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import MdiPhoneRotatePortrait from '~icons/mdi/phone-rotate-portrait'
import { UITooltipTriggerWrapper } from 'src/components/ui/Tooltip'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { useMe } from 'src/services/helpers/user'
import { useModelVersion } from 'src/stores/model'
import { isMMDIT26AModel } from 'src/utils/model'
import { emptyHiResParams, isHiResEnabled } from 'src/utils/task'
import { useTaskSubmitFormContext } from '../../../BaseTaskSubmitForm'
import {
  type AspectRatio,
  calculateSizeFromRatioAndTier,
  EXTRA_ASPECT_RATIOS_MMDIT26A,
  getOrientedAspectRatio,
  type Orientation,
  type SizeTier,
} from '../../../constants'
import { DEFAULT_XL_SIZE } from '../../constants'
import {
  calculateSizeFromRatioAndRange,
  findClosestRangeBySize,
} from './apiAdapter'
import {
  findClosestAspectRatio,
  findClosestSizeTier,
  getModelSizeDivisor,
} from './helper'
import { ImageSizeSelectModal, selectableButton } from './modal'
import { AspectRatioIcon, SizeTierSelector } from './selectors'
import { useSizeConfig } from './useSizeConfig'

/** Ratios shown as quick-select buttons in the sidebar (V1 fallback) */
const SIDEBAR_RATIOS: AspectRatio[] = [
  { label: '3:5', width: 3, height: 5 },
  { label: '1:1', width: 1, height: 1 },
  { label: '9:16', width: 9, height: 16 },
  { label: '3:4', width: 3, height: 4 },
]

export function useApplySize() {
  const { t } = useTranslation(['generation'])
  const { setValues, values } = useTaskSubmitFormContext()

  return useCallback(
    (size: { width: number; height: number }) => {
      const shouldResetHires = isHiResEnabled(values.upscale)
      setValues(prev => ({
        ...prev,
        ...size,
        ...(shouldResetHires ? emptyHiResParams : {}),
      }))
      if (shouldResetHires) {
        toast.info(t('generation:hires.notice.settings-reset-by-size-change'))
      }
    },
    [setValues, values.upscale, t],
  )
}

export function SidebarButtonGroup() {
  const { t } = useTranslation(['generation'])
  const { beingMember } = useMe()
  const { values } = useTaskSubmitFormContext()
  const applySize = useApplySize()

  const modelVersion = useModelVersion(values.modelId)
  const modelType = modelVersion?.modelType ?? GenerationModelType.SdxlModel

  const sizeConfig = useSizeConfig(values.modelId)
  const {
    ranges,
    sidebarRanges,
    sidebarRatios: apiSidebarRatios,
    isApiDriven,
    isLoading,
  } = sizeConfig

  const sidebarDivisor = getModelSizeDivisor({
    modelType,
    versionExtra: modelVersion?.extra,
  })
  const isMMDIT26A = isMMDIT26AModel(modelType)
  const extraRatios = isMMDIT26A ? EXTRA_ASPECT_RATIOS_MMDIT26A : undefined

  const sidebarRatios = isApiDriven ? apiSidebarRatios : SIDEBAR_RATIOS

  const currentRange = useMemo(() => {
    if (!isApiDriven || sidebarRanges.length === 0) return undefined
    return findClosestRangeBySize(
      values.width ?? DEFAULT_XL_SIZE.width,
      values.height ?? DEFAULT_XL_SIZE.height,
      sidebarRanges,
    )
  }, [isApiDriven, values.width, values.height, sidebarRanges])

  const currentTier = useMemo(
    () =>
      isApiDriven
        ? (currentRange?.name ?? 'M')
        : findClosestSizeTier(
            values.width ?? DEFAULT_XL_SIZE.width,
            values.height ?? DEFAULT_XL_SIZE.height,
            modelType,
          ),
    [isApiDriven, currentRange, values.width, values.height, modelType],
  )

  const currentRatio = useMemo(
    () =>
      findClosestAspectRatio(
        values.width ?? DEFAULT_XL_SIZE.width,
        values.height ?? DEFAULT_XL_SIZE.height,
        isApiDriven
          ? apiSidebarRatios
          : currentTier === 'large'
            ? extraRatios
            : undefined,
        isApiDriven,
      ),
    [
      values.width,
      values.height,
      currentTier,
      extraRatios,
      isApiDriven,
      apiSidebarRatios,
    ],
  )

  const derivedOrientation: Orientation = useMemo(
    () =>
      (values.width ?? 0) > (values.height ?? 0) ? 'landscape' : 'portrait',
    [values.width, values.height],
  )
  const isSquare = values.width != null && values.width === values.height
  const [squareOrientation, setSquareOrientation] = useState(derivedOrientation)
  // Sync orientation from non-square so switching back to 1:1 inherits direction
  useEffect(() => {
    if (!isSquare) setSquareOrientation(derivedOrientation)
  }, [isSquare, derivedOrientation])
  const orientation = isSquare ? squareOrientation : derivedOrientation

  const calculateSize = useCallback(
    (ratio: AspectRatio, tierOrRange: string) => {
      if (isApiDriven) {
        const range = ranges.find(r => r.name === tierOrRange)
        if (range) return calculateSizeFromRatioAndRange(ratio, range)
      }
      return calculateSizeFromRatioAndTier(
        ratio,
        tierOrRange as SizeTier,
        modelType,
        sidebarDivisor.width,
      )
    },
    [isApiDriven, ranges, modelType, sidebarDivisor.width],
  )

  const handleTierChange = useCallback(
    (tier: SizeTier | string) => {
      if (isApiDriven) {
        const newRange = ranges.find(r => r.name === tier)
        if (newRange) {
          const newSize = calculateSizeFromRatioAndRange(currentRatio, newRange)
          applySize(newSize)
        }
        return
      }
      const isExtraRatio = extraRatios?.some(
        r =>
          (r.width === currentRatio.width &&
            r.height === currentRatio.height) ||
          (r.width === currentRatio.height && r.height === currentRatio.width),
      )
      const ratio =
        tier !== 'large' && isExtraRatio
          ? findClosestAspectRatio(currentRatio.width, currentRatio.height)
          : currentRatio
      const newSize = calculateSizeFromRatioAndTier(
        ratio,
        tier as SizeTier,
        modelType,
        sidebarDivisor.width,
      )
      applySize(newSize)
    },
    [
      isApiDriven,
      ranges,
      currentRatio,
      modelType,
      sidebarDivisor.width,
      applySize,
      extraRatios,
    ],
  )

  const handleTranspose = useCallback(() => {
    if (values.width && values.height) {
      if (values.width === values.height) {
        // Swapping equal dimensions is a no-op; toggle squareOrientation instead
        setSquareOrientation(prev =>
          prev === 'portrait' ? 'landscape' : 'portrait',
        )
      } else {
        applySize({ width: values.height, height: values.width })
      }
    }
  }, [values.width, values.height, applySize])

  const presetSelected = useMemo(
    () =>
      sidebarRatios.some(ratio => {
        const oriented = getOrientedAspectRatio(ratio, orientation)
        const size = calculateSize(oriented, currentTier)
        return size.width === values.width && size.height === values.height
      }),
    [
      sidebarRatios,
      orientation,
      currentTier,
      calculateSize,
      values.width,
      values.height,
    ],
  )

  if (isLoading && sidebarRatios.length === 0) {
    return (
      <div className="w-full gap-1.5 grid grid-cols-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-[52px] rounded-lg bg-neutral-100 dark:bg-neutral-700 animate-pulse"
          />
        ))}
      </div>
    )
  }

  return (
    <>
      <div
        className="w-full gap-1.5 grid"
        style={{
          gridTemplateColumns: `repeat(${sidebarRatios.length + 1}, minmax(0, 1fr))`,
        }}
      >
        {sidebarRatios.map(ratio => {
          const oriented = getOrientedAspectRatio(ratio, orientation)
          const size = calculateSize(oriented, currentTier)
          const selected =
            size.width === values.width && size.height === values.height

          return (
            <Button
              key={ratio.label}
              className={selectableButton({
                selected,
                className:
                  'flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 rounded-lg',
              })}
              onPress={() => applySize(size)}
            >
              <AspectRatioIcon ratio={oriented} />
              <span className="text-xs font-medium">{oriented.label}</span>
            </Button>
          )
        })}
        <div className="flex flex-col gap-1">
          <UITooltipTriggerWrapper
            trigger={
              <Button
                aria-label={t(
                  'generation:size.transpose',
                  'Toggle orientation',
                )}
                className={selectableButton({
                  selected: false,
                  className:
                    'flex items-center justify-center py-1.5 px-1.5 rounded-lg flex-[2]',
                })}
                onPress={handleTranspose}
              >
                <MdiPhoneRotatePortrait className="w-6 h-6 text-zinc-500 dark:text-zinc-300" />
              </Button>
            }
            placement="top"
          >
            <p className="px-2 py-1">
              {t('generation:size.transpose', 'Toggle orientation')}
            </p>
          </UITooltipTriggerWrapper>
          <ImageSizeSelectModal
            key={`${values.width}-${values.height}`}
            width={values.width}
            height={values.height}
            onConfirm={val => applySize(val)}
            moreSelected={!presetSelected}
            sizeConfig={sizeConfig}
          />
        </div>
      </div>
      <SizeTierSelector
        selectedTier={currentTier}
        selectedRatio={currentRatio}
        onChange={handleTierChange}
        modelType={modelType}
        isMember={beingMember}
        divisor={sidebarDivisor.width}
        label={t('generation:size.tier.resolution-label', 'Resolution')}
        currentSize={
          !presetSelected && values.width && values.height
            ? { width: values.width, height: values.height }
            : undefined
        }
        apiRanges={isApiDriven ? sidebarRanges : undefined}
      />
    </>
  )
}
